import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Store, Package, FileText, ArrowRight, ShoppingCart, DollarSign,
  TrendingUp, Users, Layers, CreditCard, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOverview() {
  const [stats, setStats] = useState({
    total_stores: 0,
    pending_requests: 0,
    total_categories: 0,
    total_products: 0,
    total_orders: 0,
    total_revenue: 0
  });
  const [earnings, setEarnings] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, requestsRes, earningsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/onboarding/requests`),
        axios.get(`${API}/earnings/all`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/orders`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setRecentRequests(requestsRes.data.slice(0, 5));
      setEarnings(earningsRes.data);
      
      const ordersData = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data.orders || []);
      setRecentOrders(ordersData.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const platformStats = earnings?.platform_stats || {};
  const totalRev = platformStats.total_revenue || 0;
  const netPlatform = (platformStats.platform_share || 0) - (platformStats.distributor_share || 0);
  const resellerShare = platformStats.reseller_share || 0;
  const distributorShare = platformStats.distributor_share || 0;
  const netPlatformPct = totalRev > 0 ? (netPlatform / totalRev * 100) : 0;
  const resellerPct = totalRev > 0 ? (resellerShare / totalRev * 100) : 0;
  const distributorPct = totalRev > 0 ? (distributorShare / totalRev * 100) : 0;

  const quickStats = [
    { label: "Total Revenue", value: `$${totalRev.toFixed(2)}`, icon: DollarSign, gradient: "from-primary to-primary/80", link: "/admin/earnings" },
    { label: "Total Orders", value: stats.total_orders, icon: ShoppingCart, gradient: "from-purple-500 to-purple-600", link: "/admin/orders" },
    { label: "Active Resellers", value: stats.total_stores, icon: Store, gradient: "from-emerald-500 to-emerald-600", link: "/admin/resellers" },
    { label: "Products", value: stats.total_products, icon: Package, gradient: "from-sky-500 to-sky-600", link: "/admin/products" },
    { label: "Pending Requests", value: stats.pending_requests, icon: FileText, gradient: "from-amber-500 to-amber-600", link: "/admin/requests" },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    confirmed: "bg-blue-100 text-blue-700",
    processing: "bg-purple-100 text-purple-700",
    shipped: "bg-primary/10 text-primary",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm">Welcome back, here's your platform overview.</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {quickStats.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className={`bg-gradient-to-br ${stat.gradient} rounded-2xl p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 group`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <stat.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 overflow-hidden">
                <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold leading-tight truncate">{stat.value}</p>
                <p className="text-xs sm:text-sm text-white/80 font-medium truncate">{stat.label}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Earnings Overview + Revenue Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Revenue Distribution Bar */}
        <div className="lg:col-span-3 card-elevated p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-slate-900">Revenue Distribution</h3>
            <Link to="/admin/earnings" className="text-sm text-primary hover:underline flex items-center gap-1">
              View details <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="flex items-center h-10 rounded-xl overflow-hidden shadow-inner bg-slate-100 mb-4">
            {totalRev > 0 ? (
              <>
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 flex items-center justify-center text-[0.625rem] font-bold text-white transition-all"
                  style={{ width: `${netPlatformPct}%` }}
                >
                  {netPlatformPct > 10 && `${netPlatformPct.toFixed(0)}%`}
                </div>
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-400 flex items-center justify-center text-[0.625rem] font-bold text-white transition-all"
                  style={{ width: `${resellerPct}%` }}
                >
                  {resellerPct > 10 && `${resellerPct.toFixed(0)}%`}
                </div>
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 flex items-center justify-center text-[0.625rem] font-bold text-white transition-all"
                  style={{ width: `${distributorPct}%` }}
                >
                  {distributorPct > 10 && `${distributorPct.toFixed(0)}%`}
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">No revenue data</div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                <span className="text-[0.625rem] font-bold uppercase tracking-wider text-emerald-600">Platform</span>
              </div>
              <p className="text-lg font-bold text-slate-900">${netPlatform.toFixed(2)}</p>
              <p className="text-[0.625rem] text-slate-400">{netPlatformPct.toFixed(1)}% of total</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                <span className="text-[0.625rem] font-bold uppercase tracking-wider text-amber-600">Resellers</span>
              </div>
              <p className="text-lg font-bold text-slate-900">${resellerShare.toFixed(2)}</p>
              <p className="text-[0.625rem] text-slate-400">{resellerPct.toFixed(1)}% of total</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                <span className="text-[0.625rem] font-bold uppercase tracking-wider text-blue-600">Distributors</span>
              </div>
              <p className="text-lg font-bold text-slate-900">${distributorShare.toFixed(2)}</p>
              <p className="text-[0.625rem] text-slate-400">{distributorPct.toFixed(1)}% of total</p>
            </div>
          </div>
        </div>

        {/* Quick Earnings Summary */}
        <div className="lg:col-span-2 card-elevated p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Financial Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-[0.625rem] text-slate-500">Pending Payouts</p>
                  <p className="text-sm font-bold text-amber-600">${(platformStats.reseller_pending || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-[0.625rem] text-slate-500">Gross Revenue</p>
                  <p className="text-sm font-bold text-slate-900">${totalRev.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Layers className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-[0.625rem] text-slate-500">Distributor Commissions</p>
                  <p className="text-sm font-bold text-blue-600">${distributorShare.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[0.625rem] text-slate-500">Net Platform Earnings</p>
                  <p className="text-sm font-bold text-emerald-600">${netPlatform.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders + Recent Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="card-elevated overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Recent Orders</h3>
            <Link to="/admin/orders" className="text-xs text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No orders yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {recentOrders.map((order) => (
                <div key={order.order_id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {order.order_number ? `#${order.order_number}` : order.order_id?.slice(0, 12)}
                      </p>
                      <p className="text-[0.625rem] text-slate-400">{order.customer_name || order.customer_email || 'Guest'} • {formatDate(order.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-[0.625rem] font-bold px-2 py-0.5 rounded-md ${statusColors[order.status] || 'bg-slate-100 text-slate-600'}`}>
                      {order.status?.toUpperCase()}
                    </span>
                    <span className="text-sm font-bold text-slate-900">${(order.total_charged || order.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Requests */}
        <div className="card-elevated overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Recent Applications</h3>
            <Link to="/admin/requests" className="text-xs text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {recentRequests.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No applications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {recentRequests.map((request) => (
                <div 
                  key={request.request_id} 
                  className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      request.status === 'pending' ? 'bg-amber-50' : request.status === 'approved' ? 'bg-emerald-50' : 'bg-red-50'
                    }`}>
                      <Store className={`w-4 h-4 ${
                        request.status === 'pending' ? 'text-amber-500' : request.status === 'approved' ? 'text-emerald-500' : 'text-red-500'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{request.business_name}</p>
                      <p className="text-[0.625rem] text-slate-400">{request.contact_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-[0.625rem] font-bold px-2 py-0.5 rounded-md ${
                      request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {request.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
